import { Box, Flex, Spinner, Text } from '@chakra-ui/react'
import { InlineLoading } from 'components'

import { IBaseView } from 'interfaces/navigationPage.interface'
import { Virtuoso } from 'react-virtuoso'
import { Card } from '../../Epidemiology'
import { useEffect, useMemo, useState } from 'react'
import { Article } from 'routes/dashboard/WidgetComponents/WidgetArticle/Article'
import { useFilteredArticles } from 'routes/dashboard/WidgetComponents/WidgetArticle/useFilteredArticles'
import { IDTreatment360NewsAndPressReleasesModel } from 'config/common/baseModel/ida360/IDTreatment360NewsAndPressReleases'
import { ArticleFiltersT, DefaultArticleFilters } from 'utils/useArticleFilters'
import {
  getAirtableNameByDisease,
  getDefaultArticleFilters,
  getDiseaseFilter,
} from 'modules/BioriskMpox/LatestAnalysisNews/LatestMedia'
import { isEqual } from 'lodash'

const SimpleCard = ({ h, children }: any) => {
  return (
    <Card minW='100%' h='100%'>
      <Text fontWeight='semibold' mb={2}>
        Latest Media
      </Text>
      <Box h={'100%'} w='full' overflow={'hidden'} mt={'10px'}>
        <Flex justifyContent='center' alignItems='center' h='80%'>
          {children}
        </Flex>
      </Box>
    </Card>
  )
}

const LatestMedia = ({
  selectedArea,
  disease,
  disableTags,
}: {
  selectedArea?: string
  disease: string
  disableTags?: boolean
}) => {
  const view = {
    airtableName: getAirtableNameByDisease(disease),
    airtableBase: 'lzdb',
    name: 'Default',
  } as IBaseView<any, any>

  const keyNews = 'keyNewsAlert'

  const model = IDTreatment360NewsAndPressReleasesModel
  const [filters, setFilters] = useState<ArticleFiltersT>(
    getDefaultArticleFilters(disease)
  )

  useEffect(() => {
    if (selectedArea) {
      const isGlobal = selectedArea === 'global'
      setFilters({
        ...DefaultArticleFilters,
        ...getDiseaseFilter(disease),
        locations: isGlobal
          ? []
          : [
              {
                label: selectedArea,
                id: selectedArea,
                value: selectedArea,
              },
            ],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea])

  const disableTracking = useMemo(
    () => isEqual(filters, getDefaultArticleFilters(disease)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disease]
  )

  const {
    data,
    canFetchMore,
    isFetched,
    isError,
    fetchMore,
    isFetchingNextPage,
  } = useFilteredArticles({
    maxItems: 10,
    model,
    view,
    filters,
    disableTracking,
  })

  if (isError || (!data?.length && isFetched) || !data?.length) {
    const message = isError
      ? 'Sorry, something went wrong loading the media'
      : !data?.length && isFetched
        ? 'No media at the moment'
        : null

    return (
      <SimpleCard>
        {message ? (
          <Text
            textAlign={'center'}
            color={isError ? 'error' : 'inherit'}
            fontWeight={'semibold'}
          >
            {message}
          </Text>
        ) : (
          <Flex w='100%' alignItems='center' justifyContent='center'>
            <InlineLoading />
          </Flex>
        )}
      </SimpleCard>
    )
  }

  return (
    <Card minW='100%' h='100%'>
      <Text fontWeight='semibold' mb={2}>
        Latest Media
      </Text>
      {data?.length === 0 && (
        <Flex justifyContent='center' alignItems='center' h='80%'>
          <Text>No media articles match selected filters.</Text>
        </Flex>
      )}
      {!!data?.length && (
        <Box
          h={'100%'}
          pb='2rem'
          w='full'
          overflow={'hidden'}
          mt={'5px'}
          ml='-1rem'
          mb='-1rem'
        >
          <Virtuoso
            totalCount={data.length}
            itemContent={(index: number) => {
              const row = data[index]
              return (
                <Article
                  id={row.id}
                  modelEndpoint={'rss-article'}
                  airtableBase={view.airtableBase}
                  key={row.id}
                  url={row.link as string}
                  date={row.publishedAt as string}
                  title={row?.titleDisplay as string}
                  tags={disableTags ? [] : (row?.tags as string[])}
                  countries={row?.areasAlpha3 as string[]}
                  isKeyNews={!!row[keyNews]}
                  trackMediaClick={() => {}}
                />
              )
            }}
            endReached={() => {
              if (canFetchMore && !isFetchingNextPage) {
                fetchMore()
              }
            }}
          />
          {isFetchingNextPage && (
            <Flex w='full' justifyContent={'center'} mt='4px'>
              <Spinner />
            </Flex>
          )}
        </Box>
      )}
    </Card>
  )
}

export default LatestMedia
